/**
 * 案件起票モーダル Step1（依頼情報）フォーム。
 * ProjectIntakeModal から分離した表示専用コンポーネント。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件起票の依頼情報入力フォーム（事業/チーム/PL/主担当/PJ名/AB/リリース希望日/ラフURL）
 * @graph-connects none
 */

import type { ReactNode } from 'react';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { Picker } from '@cortex/web-design-system/components/ui/Picker';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import { Label } from '@cortex/web-design-system/components/ui/Label';
import { type ProjectKind } from '../../../types/project-intake.js';
import { Field, ChipSelect, businessTone, teamTone } from './ProjectIntakeControls.js';
import type { FormState, SetFormField } from './form-state.js';

/** 選択肢（value/label）の配列。 @graph-connects none */
type Options = { value: string; label: string }[];

/** @graph-connects none */
export interface ProjectIntakeStep1FormProps {
  /** フォーム状態 */
  form: FormState;
  /** フォーム 1 項目のセッター */
  set: SetFormField;
  /** 未入力時にエラーメッセージを返す関数 */
  err: (missing: boolean) => string | undefined;
  /** ラフ URL の形式エラー（未入力・正常時は undefined） */
  roughUrlError?: string;
  /** 選択肢の読み込み中フラグ */
  loading: boolean;
  /** 案件種類の選択肢 */
  kindOptions: Options;
  /** 事業の選択肢 */
  businessOptions: Options;
  /** 担当チームの選択肢 */
  teamOptions: Options;
  /** メンバー（PL/主担当）の選択肢 */
  memberOptions: Options;
}

/**
 * Step1（依頼情報）の入力グリッド。
 * @graph-connects none
 */
export function ProjectIntakeStep1Form({
  form,
  set,
  err,
  roughUrlError,
  loading,
  kindOptions,
  businessOptions,
  teamOptions,
  memberOptions,
}: ProjectIntakeStep1FormProps): ReactNode {
  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
      <Field label="案件種類 *">
        <Select
          placeholder="選択してください"
          options={kindOptions}
          value={form.kind}
          error={err(!form.kind)}
          onChange={(e) => {
            set('kind', e.target.value as ProjectKind);
          }}
        />
      </Field>
      <Field label="PJ名 *">
        <Input
          placeholder="プロジェクト名"
          value={form.projectName}
          error={err(!form.projectName.trim())}
          onChange={(e) => {
            set('projectName', e.target.value);
          }}
        />
      </Field>
      <Field label="事業 *">
        <ChipSelect
          value={form.business}
          options={businessOptions}
          placeholder={loading ? '読み込み中...' : '選択してください'}
          toneFor={businessTone}
          error={err(!form.business)}
          onChange={(v) => {
            set('business', v);
            set('team', '');
          }}
        />
      </Field>
      <Field label="担当チーム *">
        <ChipSelect
          value={form.team}
          options={teamOptions}
          placeholder={form.business ? '選択してください' : '先に事業を選択'}
          disabled={!form.business}
          toneFor={teamTone}
          error={err(!form.team)}
          onChange={(v) => {
            set('team', v);
          }}
        />
      </Field>
      <Field label="PL *">
        <Picker
          options={memberOptions}
          value={form.plNickname || null}
          onChange={(v) => {
            set('plNickname', v ?? '');
          }}
          placeholder={loading ? '読み込み中...' : '選択してください'}
          searchable
          searchPlaceholder="nickname で検索..."
          emptyMessage="該当なし"
          aria-label="PL"
          className="w-full"
        />
        {err(!form.plNickname) && (
          <p className="mt-1 text-xs text-[var(--color-error)]">必須項目です</p>
        )}
      </Field>
      <Field label="主担当 *">
        <Picker
          options={memberOptions}
          value={form.mainWorkerNickname || null}
          onChange={(v) => {
            set('mainWorkerNickname', v ?? '');
          }}
          placeholder={loading ? '読み込み中...' : '選択してください'}
          searchable
          searchPlaceholder="nickname で検索..."
          emptyMessage="該当なし"
          aria-label="主担当"
          className="w-full"
        />
        {err(!form.mainWorkerNickname) && (
          <p className="mt-1 text-xs text-[var(--color-error)]">必須項目です</p>
        )}
      </Field>
      <Field label="リリース希望日">
        <Input
          type="date"
          value={form.releaseHopeDate}
          helperText="チケットのみに反映（案件仕様書には回填しません）"
          onChange={(e) => {
            set('releaseHopeDate', e.target.value);
          }}
        />
      </Field>
      <Field label="ABテスト案件？">
        <Label
          htmlFor="intake-abtest"
          className="flex h-[42px] cursor-pointer items-center gap-2 text-sm text-[var(--color-text-secondary)]"
        >
          <Checkbox
            id="intake-abtest"
            checked={form.isAbTest}
            onChange={(e) => {
              set('isAbTest', e.target.checked);
            }}
          />
          はい（AB テスト対象）
        </Label>
      </Field>
      <Field label="ラフイメージ・機能のURL" className="sm:col-span-2">
        <Input
          type="url"
          placeholder="https://..."
          value={form.roughImageUrl}
          error={roughUrlError}
          onChange={(e) => {
            set('roughImageUrl', e.target.value);
          }}
        />
      </Field>
    </div>
  );
}
